import type { AgentWorker } from '../types/agentConfigurator';

/** Case-insensitive duplicate keys among coordinator + workers. */
export function getDuplicateAgentNameKeys(
  coordinatorName: string | undefined,
  workers: AgentWorker[]
): Set<string> {
  const counts = new Map<string, number>();
  const bump = (raw: string | undefined) => {
    const key = String(raw || '').trim().toLowerCase();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  };
  bump(coordinatorName);
  workers.forEach((w) => bump(w.name));
  const duplicates = new Set<string>();
  counts.forEach((count, key) => {
    if (count > 1) duplicates.add(key);
  });
  return duplicates;
}

export function getAgentNameUniquenessError(
  coordinatorName: string | undefined,
  workers: AgentWorker[]
): string | null {
  const duplicateKeys = getDuplicateAgentNameKeys(coordinatorName, workers);
  if (duplicateKeys.size === 0) return null;
  const firstDup = workers.find((w) => duplicateKeys.has(String(w.name || '').trim().toLowerCase()));
  const fallback = String(coordinatorName || '').trim();
  const duplicatedName = String(firstDup?.name || fallback || 'agent');
  return `Agent name "${duplicatedName}" is duplicated. Agent names must be unique.`;
}

/** Suggest a worker name not used by coordinator or existing workers (A–Z, a–z, 0–9 only). */
export function suggestUniqueAgentName(
  coordinatorName: string | undefined,
  workers: AgentWorker[]
): string {
  const used = new Set<string>();
  const mark = (raw: string | undefined) => {
    const key = String(raw || '').trim().toLowerCase();
    if (key) used.add(key);
  };
  mark(coordinatorName);
  workers.forEach((w) => mark(w.name));
  for (let i = 1; i <= workers.length + 99; i += 1) {
    const candidate = i === 1 ? 'Agent' : `Agent${i}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return `Agent${Date.now()}`;
}
